import React from 'react'
import { Button } from 'antd'

/**
 * 分块加载失败兜底。
 * <p>
 * 后台路由是 `lazy(() => import(...))` 按页分包，`index.html` 引用带 hash 的 main/分块文件。
 * 发版时部署脚本会 `sync --delete` 删掉上一版分块：若用户标签页还停留在旧版本（或浏览器缓存了旧 HTML），
 * 点进一个尚未加载过的页面就会用旧 hash 去请求已删除的文件 → `ChunkLoadError` → React 整树卸载，
 * 表现是**白屏且没有任何接口请求**（组件根本没挂载）。
 * <p>
 * 恢复动作只能是重新加载入口 HTML（部署脚本已把 index.html 设为 no-cache，reload 会拿到新版本
 * 与新的分块映射）。这里做两件事：
 * <ol>
 *   <li>识别到分块错误时自动 reload 一次（会话内 10s 冷却，且 storage 不可用时不自动重载，避免死循环）；</li>
 *   <li>冷却中仍失败（或不是分块错误）时展示兜底页，给一个「重新加载」按钮，而不是留一片白屏。</li>
 * </ol>
 */
const CHUNK_ERROR_PATTERN = /ChunkLoadError|Loading (?:CSS )?chunk .* failed|Importing a module script failed/i

const RELOAD_FLAG = 'padi:chunkReloadAt'
// 冷却窗口要比一次 reload 明显长：10s 内再次失败说明不是「资源换版」导致，继续刷新只会死循环。
const RELOAD_COOLDOWN_MS = 10 * 1000

const isChunkLoadError = (error) => {
  if (!error) return false
  return CHUNK_ERROR_PATTERN.test(`${error.name || ''} ${error.message || ''}`)
}

const canAutoReload = () => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) || 0)
    return !last || Date.now() - last > RELOAD_COOLDOWN_MS
  } catch (e) {
    // 隐私模式 / 禁用 storage：没有去重能力就不要自动 reload，避免刷新死循环
    return false
  }
}

const markReload = () => {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
  } catch (e) {
    // 忽略：记录失败最多导致少一次自动重载，不影响兜底页
  }
}

class ChunkLoadErrorBoundary extends React.Component {
  state = { failed: false, chunkError: false }

  static getDerivedStateFromError(error) {
    return { failed: true, chunkError: isChunkLoadError(error) }
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error) && canAutoReload()) {
      markReload()
      window.location.reload()
    }
  }

  handleReload = () => {
    markReload()
    window.location.reload()
  }

  render() {
    if (!this.state.failed) return this.props.children
    const chunkError = this.state.chunkError
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          color: '#333',
        }}
      >
        <h3 style={{ marginBottom: 8 }}>{chunkError ? '页面资源已更新' : '页面出错了'}</h3>
        <p style={{ marginBottom: 16, color: '#666' }}>
          {chunkError
            ? '检测到页面资源版本已更新，请重新加载后继续操作。'
            : '页面加载时发生异常，请重新加载；若仍失败请联系技术支持。'}
        </p>
        <Button type="primary" onClick={this.handleReload}>
          重新加载
        </Button>
      </div>
    )
  }
}

export default ChunkLoadErrorBoundary
export { isChunkLoadError }
